import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowDown } from "lucide-react";
import { LineReveal, Reveal } from "./Reveal";
import { PHONE, PHONE_HREF, IMAGES, scrollToIntake } from "../../lib/erg";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden flex items-end pb-16 sm:pb-24 pt-24"
    >
      {/* Underwater image */}
      <motion.div className="absolute inset-0 -z-20" style={{ y: reduce ? 0 : imgY }}>
        <img
          src={IMAGES.heroUnderwater}
          alt=""
          className="h-full w-full object-cover object-center opacity-40"
        />
      </motion.div>

      {/* Deep water wash */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-erg-deep/70 via-erg-deep/85 to-erg-deep" />

      {/* Caustic light ripple */}
      <motion.div
        className="absolute inset-0 -z-10 caustic-layer animate-caustic-drift"
        style={{ opacity: reduce ? 0.5 : overlayOpacity }}
      />

      <motion.div
        style={{ y: reduce ? 0 : contentY }}
        className="relative mx-auto max-w-6xl w-full px-5 sm:px-8"
      >
        <Reveal delay={0.05}>
          <div className="inline-flex items-center gap-2 rounded-full border border-erg-aqua/30 bg-erg-aqua/5 px-3 py-1 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-erg-aqua animate-pulse" />
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-erg-aqua/90">
              24/7 emergency water response
            </span>
          </div>
        </Reveal>

        <LineReveal
          className="font-display font-bold text-erg-dry uppercase tracking-tight leading-[0.92] text-[clamp(2.6rem,9vw,5.75rem)] max-w-[15ch]"
          lines={["Flooded?", "Here's exactly what", "to do in the next hour."]}
        />

        <Reveal delay={0.7} className="mt-7 max-w-xl">
          <p className="text-erg-dry/80 text-lg sm:text-xl leading-relaxed text-pretty">
            Take a breath. You haven't done anything wrong, and you're not too late.
            We've walked thousands of homeowners through exactly this. Here's the calm
            version of what happens next.
          </p>
        </Reveal>

        <Reveal delay={0.85} className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button
            data-testid="hero-intake-cta"
            onClick={scrollToIntake}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-erg-ember px-7 py-4 sm:px-10 sm:py-5 font-display font-semibold uppercase tracking-wide text-erg-ink text-base sm:text-xl hover:brightness-105 active:scale-[0.98] transition-[transform,filter] duration-200 ember-pulse"
          >
            Start your intake
            <ArrowDown className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-y-0.5 transition-transform duration-300" />
          </button>
          <a
            data-testid="hero-phone-link"
            href={PHONE_HREF}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-erg-dry/25 bg-white/5 px-7 py-4 sm:px-10 sm:py-5 text-erg-dry text-base sm:text-xl font-medium hover:border-erg-aqua/60 hover:bg-white/10 transition-colors duration-300"
          >
            <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
            Call or text {PHONE}
          </a>
        </Reveal>

        <Reveal delay={1} className="mt-6 flex flex-col sm:flex-row sm:items-center gap-x-5 gap-y-3">
          <p className="font-mono text-[12px] tracking-wide text-erg-dry/45">
            No obligation. No quotes to chase first. Just water out, drying started.
          </p>
          <span
            data-testid="live-response-badge"
            className="inline-flex items-center gap-2.5 self-start rounded-full border border-erg-aqua/25 bg-erg-aqua/[0.06] pl-2.5 pr-3.5 py-1.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="live-ping absolute inline-flex h-full w-full rounded-full bg-erg-aqua" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-erg-aqua" />
            </span>
            <span className="font-mono text-[11px] tracking-wide text-erg-aqua/90">
              Crews available near you right now
            </span>
          </span>
        </Reveal>
      </motion.div>

      <motion.div
        aria-hidden="true"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-erg-dry/40"
        animate={reduce ? {} : { y: [0, 8, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <ArrowDown className="h-5 w-5" />
      </motion.div>
    </section>
  );
};
